import { Billable, TurnOverItem } from '../../../config/registration/timeConfig'
import { InvoiceStatus, ITimeItemDto } from '../../../config/finance/invoiceConfig'
import DetailsListAbstract, {
    ICoreColumn,
    ICoreDetailsListFilter,
    ICoreDetailsListSettings,
} from '../../DetailsList/DetailsListAbstract'
import {
    fileColumn,
    includeColumnFilter,
    moneyColumn,
    multiSelectFilter,
    personaColumn,
    shortDateColumn,
} from '../../../helpers/detailsListHelper'
import {
    CheckboxVisibility,
    DetailsRow,
    IColumn,
    Icon,
    IDetailsHeaderProps,
    ISelection,
    Label,
    mergeStyles,
    SelectionMode,
    Stack,
    StackItem,
    Text,
} from '@fluentui/react'
import { Link } from 'react-router-dom'
import { multiplyTimeRate, reduceCurrencies, reduceFormattedDuration } from '../../../helpers/numberHelper'
import { timeBaseUrl } from '../../../pages/Times'
import * as React from 'react'
import { ReactElement, useEffect, useRef, useState } from 'react'
import { IRenderFunction } from '@fluentui/utilities'
import { MoneyDto } from '../../../config/i18nConfig'
import TimeText from '../../Text/Time/TimeText'
import MoneyText from '../../Text/Currency/MoneyText'
import { parseDate } from '../../../helpers/dateHelper'
import { TimeOverviewDto } from '../../../config/registrationConfig'
import Tooltip from '../../../components/Tooltip'
import { IIconProps } from '@fluentui/react/lib/components/Icon/Icon.types'
import useModules, { ModulesProps } from '../../../hooks/module/useModules'
import { ModuleType } from '../../../context/GlobalContext'
import { AttributeEntityDto } from '../../../hooks/useAttributes'
import { useTranslation } from 'react-i18next'
import { BillableLabels } from '../../TagPicker/BillablePicker'
import { InvoiceStatusLabels } from '../../TagPicker/InvoiceStatusPicker'

type BaseItem = TimeOverviewDto | ITimeItemDto

export type OverviewItem = {
    // Is calculated during runtime and not actually returned by the API. Expressed in American number notation (0.00).
    turnover: MoneyDto
    invoiceTurnover: MoneyDto
} & BaseItem &
    TurnOverItem

export type SummaryItem = {
    duration?: OverviewItem['duration']
    turnover?: OverviewItem['turnover'][]
    invoiceTurnover?: OverviewItem['invoiceTurnover'][]
}

export type ITimeListSettings = ICoreDetailsListSettings & {
    filter: TimeListFilter
}

export type TimeListFilter = AttributeEntityDto & {
    from?: string
    to?: string
    users?: string[]
    isBilled?: boolean
    files?: string[]
    fileOwners?: string[]
    billable?: Billable[]
}

export type TimeListProps = {
    data?: TimeOverviewDto[] | ITimeItemDto[]
    baseUrl: string
    global?: boolean // If the file column should be shown.
    billing?: boolean // If this list contains billing info.
    filters?: ICoreDetailsListFilter<OverviewItem, ITimeListSettings>[]
    settings?: ITimeListSettings
    selection?: ISelection
    columns?: Partial<ICoreColumn>[]
}
export const getTimeId = (item: TimeOverviewDto): string =>
    // Some items are an instance of TimeItem, while others are an instance of TimeOverview. These are different entities.
    item.hasOwnProperty('timeId') ? (item as ITimeItemDto).id : item.id
export const getTimeTurnover = (rate: MoneyDto, item: TurnOverItem): MoneyDto => {
    if (!rate) {
        return undefined
    }

    if (!item || item.billable !== Billable.Billable) {
        return {
            amount: '0.00',
            currency: rate.currency,
        }
    }

    // An item only has the turnover property after the filters have applied during the mounting of this TimeList,
    // therefore calculate it when no turnover has been calculated.
    return multiplyTimeRate(item.duration, rate)
}
const isSummary = (item: OverviewItem | SummaryItem): item is SummaryItem => {
    return Array.isArray(item.turnover)
}

export const getTurnoverIconProps = (item: TurnOverItem, index: number): IIconProps => {
    if (item.billable === Billable.Billable || index === -1) {
        return { iconName: 'Completed', className: 'ms-fontColor-green' }
    }
    return { iconName: 'Cancel', className: 'ms-fontColor-red' }
}

function TurnoverRateTooltip({ item }: { item: OverviewItem }): ReactElement {
    const { t: translate } = useTranslation()

    return (
        <Stack tokens={{ childrenGap: 'm' }}>
            <Stack grow tokens={{ childrenGap: 's2' }}>
                <Label>Uurtarief</Label>
                <StackItem>
                    <MoneyText value={item.rate} className={mergeStyles({ justifyContent: 'left' })} />
                </StackItem>
            </Stack>
            <Stack grow tokens={{ childrenGap: 's2' }}>
                <Label>Declareerbaar</Label>
                <Text>{translate(BillableLabels.get(item.billable).name)}</Text>
            </Stack>
        </Stack>
    )
}

function InvoiceRateTooltip({ item }: { item: OverviewItem }): ReactElement {
    const { t: translate } = useTranslation()

    return (
        <Stack tokens={{ childrenGap: 'm' }}>
            <Stack grow tokens={{ childrenGap: 's2' }}>
                <Label>Declaratienummer</Label>
                <StackItem>
                    <Text>{item.invoice.number}</Text>
                </StackItem>
            </Stack>
            <Stack grow tokens={{ childrenGap: 's2' }}>
                <Label>Status</Label>
                <Text>{translate(InvoiceStatusLabels.get(item.invoice.status).name)}</Text>
            </Stack>
        </Stack>
    )
}

const getColumns = (modules: ModulesProps, translate: (key: string) => string, baseUrl: string): ICoreColumn[] =>
    [
        personaColumn({
            key: 'user',
        }),
        shortDateColumn({
            key: 'date',
            isSortedDescending: true,
            isSorted: true,
        }),
        {
            key: 'duration',
            name: 'Tijd',
            fieldName: 'duration',
            minWidth: 42,
            maxWidth: 42,
            isResizable: true,
            isRowHeader: true,
            isIconOnly: true,
            isSortable: true,
            iconName: 'Timer',
            className: 'ms-textAlignRight',
            headerClassName: 'align-center',
            onRender: (item?: OverviewItem) => <TimeText value={item.duration} />,
        },
        {
            key: 'description',
            name: translate('base.table.header.description'),
            fieldName: 'description',
            minWidth: 160,
            isResizable: true,
            isRowHeader: true,
            isSortable: true,
            onRender: (item?: OverviewItem, index?: number) => {
                const inner = (
                    <div className="time item-open">
                        {[InvoiceStatus.Final, InvoiceStatus.Done].includes(item.invoice?.status) && (
                            <Icon iconName={'Lock'} className="inline-lock" />
                        )}
                        {item.description || <em>Omschrijving ontbreekt.</em>}
                    </div>
                )

                // Only render when it's an actual row, and not a header.
                if (index !== -1) {
                    // Only render a link when the TimeOverview is not an orphaned TimeItem.
                    return getTimeId(item) !== null ? (
                        <Link key={`${getTimeId(item)}-edit`} to={`${baseUrl}/${getTimeId(item)}/edit`}>
                            {inner}
                        </Link>
                    ) : (
                        inner
                    )
                }
                return null
            },
        },
        moneyColumn({
            key: 'turnover',
            name: 'Bedrag',
            minWidth: 100,
            maxWidth: 120,
            filter: () => modules.has(ModuleType.Invoices),
            onRender: (item?: OverviewItem | SummaryItem, index?: number) => {
                if (!item) {
                    return <></>
                } else if (isSummary(item)) {
                    return item.turnover.map(value => (
                        <Stack key={value.currency}>
                            <MoneyText value={value} align="right" />
                        </Stack>
                    ))
                }
                return (
                    <Tooltip hostProps={{ content: <TurnoverRateTooltip item={item} /> }}>
                        <Stack horizontal tokens={{ childrenGap: 's2' }}>
                            <StackItem grow>
                                <MoneyText value={item.turnover} align="right" />
                            </StackItem>
                            <StackItem>
                                <Icon {...getTurnoverIconProps(item, index)} />
                            </StackItem>
                        </Stack>
                    </Tooltip>
                )
            },
        }),
        fileColumn({ key: 'file' }, timeBaseUrl),
        moneyColumn<OverviewItem>({
            key: 'invoiceTurnover',
            name: translate('time.table.header.turnover'),
            isIconOnly: false,
            filter: () => modules.has(ModuleType.Invoices),
            onRender: (item?: OverviewItem | SummaryItem) => {
                if (isSummary(item)) {
                    return item.invoiceTurnover.map(value => (
                        <MoneyText value={value} key={value.currency} align="right" />
                    ))
                } else if (item?.invoice && item?.invoiceTurnover) {
                    return (
                        <Tooltip hostProps={{ content: <InvoiceRateTooltip item={item} /> }}>
                            <Link to={`/finance/invoices/${item.invoice.id}/edit`}>
                                <MoneyText value={item.invoiceTurnover} textProps={{ size: 300 }} align="right" />
                            </Link>
                        </Tooltip>
                    )
                }
                return <></>
            },
        }),
    ].filter(column => !column.filter || column.filter())

const filters: ICoreDetailsListFilter<OverviewItem, ITimeListSettings>[] = [
    multiSelectFilter(
        (settings: ITimeListSettings) => settings.filter?.users,
        (item: OverviewItem) => item.user?.id,
    ),

    // Select items that are within the date range.
    (items: OverviewItem[], settings: ITimeListSettings) =>
        settings.filter?.from || settings.filter?.to
            ? items.filter((item: OverviewItem) => {
                  if (!settings.filter?.from) {
                      return parseDate(item.date) <= parseDate(settings.filter?.to)
                  }
                  if (!settings.filter?.to) {
                      return parseDate(item.date) >= parseDate(settings.filter?.from)
                  }
                  return (
                      parseDate(item.date) >= parseDate(settings.filter?.from) &&
                      parseDate(item.date) <= parseDate(settings.filter?.to)
                  )
              })
            : items,

    (items: OverviewItem[], settings: ITimeListSettings) => {
        return settings.filter?.billable !== undefined &&
            settings.filter?.billable !== null &&
            settings.filter?.billable?.length > 0
            ? items.filter((item: OverviewItem) =>
                  (settings.filter?.billable || []).some((value: Billable) => value === item.billable),
              )
            : items
    },

    (items: OverviewItem[], settings: ITimeListSettings) => {
        return settings.filter?.isBilled !== undefined && settings.filter?.isBilled !== null
            ? // This is basically an XOR statement (either has no invoice xor only show billed).
              items.filter((item: OverviewItem) => (item.invoice !== null) === settings.filter?.isBilled)
            : items
    },

    // Calculate the turnover associated with each item.
    (items: OverviewItem[]) =>
        items.map((item: OverviewItem) => ({
            ...item,
            turnover: getTimeTurnover(item.rate, item),
            invoiceTurnover: item.price && {
                amount: item.price.subtotal,
                currency: item.price.currency,
            },
        })),
]

const getSummary = (items: OverviewItem[]) => ({
    duration: reduceFormattedDuration(items),
    turnover: reduceCurrencies(items, item => item.turnover),
    invoiceTurnover: reduceCurrencies(items, item => item.invoiceTurnover),
})

export default function TimeList(props: TimeListProps): ReactElement {
    const ref = useRef<DetailsListAbstract<OverviewItem>>()
    const [columns, setColumns] = useState<ICoreColumn[]>([])
    const modules = useModules()
    const { t: translate } = useTranslation()

    useEffect(() => {
        setColumns(
            getColumns(modules, translate, props.baseUrl)
                .filter(column => includeColumnFilter(column, ['file'], props.global))
                .filter(column => includeColumnFilter(column, ['invoiceNumber', 'invoiceTurnover'], props.billing))
                .map(column => ({ ...column, ...props.columns?.find(({ key }: IColumn) => key === column.key) })),
        )
    }, [props.baseUrl, props.global, props.billing])

    return (
        <DetailsListAbstract<OverviewItem>
            onRenderDetailsHeader={(
                detailsHeaderProps: IDetailsHeaderProps,
                defaultRender: IRenderFunction<IDetailsHeaderProps>,
            ) => (
                <>
                    {defaultRender(detailsHeaderProps)}
                    <DetailsRow
                        item={getSummary(
                            (props.selection
                                ? (ref.current?.state.selection.getSelection() as OverviewItem[])
                                : ref.current?.state.items) || [],
                        )}
                        itemIndex={-1}
                        {...(detailsHeaderProps as any)}
                    />
                </>
            )}
            ref={ref}
            columns={columns}
            items={(props.selection ? props.selection.getItems() : props.data) as OverviewItem[]}
            filters={[...filters, ...(props.filters || [])]}
            settings={props.settings}
            isSortable
            emptyDescription={translate('base.table.empty')}
            {...(props.selection && {
                selection: props.selection,
                selectionMode: SelectionMode.multiple,
                selectionPreservedOnEmptyClick: true,
                checkboxVisibility: CheckboxVisibility.always,
            })}
        />
    )
}
